import { Message } from '../hooks/useChat'
import { Conversation } from '../hooks/useChatGraphQL'

export type ChannelType = 'WHATSAPP' | 'SMS' | 'TELEGRAM' | 'WIDGET'

export interface ChannelBadgeInfo {
  icon: string
  label: string
  shortPhone: string
  fullPhone: string
  provider: string
  color: string
  backgroundColor: string
}

export interface ChannelBadgeConfig {
  icon: string
  label: string
  color: string
  backgroundColor: string
}

export const CHANNEL_BADGE_CONFIG: Record<ChannelType, ChannelBadgeConfig> = {
  WHATSAPP: { icon: 'bi-whatsapp', label: 'WA', color: '#fff', backgroundColor: '#25d366' },
  SMS: { icon: 'bi-chat-dots', label: 'SMS', color: '#fff', backgroundColor: '#3e97ff' },
  TELEGRAM: { icon: 'bi-telegram', label: 'TG', color: '#fff', backgroundColor: '#26a5e4' },
  WIDGET: { icon: 'bi-window', label: 'WEB', color: '#fff', backgroundColor: '#6c757d' },
}

const isChannelType = (value: unknown): value is ChannelType =>
  typeof value === 'string' && value in CHANNEL_BADGE_CONFIG

// Resolves the actual channel of a conversation's last message, preferring the
// most specific/reliable source available. GraphQL fetches populate
// `received_via_channel`; optimistic sends and WebSocket updates only populate
// the plain `channel` string; conversations without message data yet fall back
// to whichever channel is configured active for the contact.
export const resolveConversationChannel = (c: Conversation): ChannelType | null => {
  const receivedVia = c.last_message?.received_via_channel?.channel_type
  if (isChannelType(receivedVia)) return receivedVia

  const plainChannel = c.last_message?.channel
  if (isChannelType(plainChannel)) return plainChannel

  const detail = c.active_channels_detail?.find((d) => isChannelType(d?.channel_type))
  if (detail && isChannelType(detail.channel_type)) return detail.channel_type

  const active = c.active_channels?.find((ch) => isChannelType(ch))
  if (isChannelType(active)) return active

  return null
}

export const getChannelBadgeInfo = (message: Message): ChannelBadgeInfo | null => {
  if (!message?.received_via_channel) return null

  const channel = message.received_via_channel
  const phoneNumber = channel.phone_number || ''
  const last4 = phoneNumber ? phoneNumber.slice(-4) : '----'
  const config = isChannelType(channel.channel_type) ? CHANNEL_BADGE_CONFIG[channel.channel_type] : null

  return {
    icon: config?.icon || 'bi-chat-dots',
    label: config?.label || channel.channel_type,
    shortPhone: `••••${last4}`,
    fullPhone: phoneNumber,
    provider: channel.provider,
    color: config?.color || '#fff',
    backgroundColor: config?.backgroundColor || '#6c757d',
  }
}

export const getProviderLabel = (provider: string): string => {
  const map: Record<string, string> = {
    TWILIO: 'Twilio',
    BOTMAKER: 'Botmaker',
    META: 'Meta',
  }
  return map[provider] || provider
}

export const getChannelBadgeTooltip = (message: Message): string => {
  if (!message?.received_via_channel) return ''
  const channel = message.received_via_channel
  return `${getProviderLabel(channel.provider)} ${channel.channel_type} ${channel.phone_number || ''}`
}
